import React, { useEffect, useState, useRef } from "react";
import { cn } from "@jammable/ui-core";

interface Word {
  word: string;
  start: number;
  end: number;
  true_end: number;
  probability: number;
}

interface TranscriptionSegment {
  id: number;
  seek: number;
  start: number;
  end: number;
  text: string;
  words: Word[];
}

interface KaraokeLyricsProps {
  transcription: TranscriptionSegment[] | null;
  currentTime: number;
  className?: string;
}

export const KaraokeLyrics: React.FC<KaraokeLyricsProps> = ({
  transcription,
  currentTime,
  className,
}) => {
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(-1);
  const [activeWordIndex, setActiveWordIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const baseLyricClassName =
    "tw-text-sm tw-font-black tw-tracking-widest tw-uppercase";
  const wordClassName = "tw-relative tw-inline-block tw-mr-[4.2px]";

  useEffect(() => {
    if (!transcription) return;

    // Find active segment and word
    let segmentIndex = -1;
    let wordIndex = -1;

    for (let i = 0; i < transcription.length; i++) {
      const segment = transcription[i];
      if (currentTime >= segment.start && currentTime < segment.end) {
        segmentIndex = i;

        // Find active word within the segment
        for (let j = 0; j < segment.words.length; j++) {
          const word = segment.words[j];
          if (currentTime >= word.start && currentTime < word.end) {
            wordIndex = j;
            break;
          }
        }
        break;
      }
    }

    setActiveSegmentIndex(segmentIndex);
    setActiveWordIndex(wordIndex);
  }, [currentTime, transcription]);

  if (!transcription || transcription.length === 0) {
    return null;
  }

  // Hide component if lyrics are finished
  const lastSegment = transcription[transcription.length - 1];
  if (currentTime > lastSegment.end) {
    return null;
  }

  const currentSegment =
    activeSegmentIndex >= 0 ? transcription[activeSegmentIndex] : null;

  // Find the next segment to show as a preview when no segment is active
  const nextSegment = !currentSegment
    ? transcription.find((segment) => segment.start > currentTime)
    : null;

  return (
    <div
      ref={containerRef}
      className={cn(
        "tw-relative tw-w-full tw-px-4 tw-flex tw-justify-center",
        className,
      )}
    >
      <div className="tw-w-max tw-items-center tw-justify-center tw-bg-black/5 tw-p-3 tw-rounded-2xl tw-text-wrap">
        {/* Current line with word-by-word highlighting */}
        {currentSegment ? (
          <div className="tw-relative tw-w-full tw-text-center tw-text-wrap">
            {currentSegment.words.map((word, index) => {
              const isActive = index === activeWordIndex;
              const isPast = index < activeWordIndex;
              const isFuture = index > activeWordIndex;

              return (
                <span
                  key={`${currentSegment.id}-${index}`}
                  className={cn(
                    baseLyricClassName,
                    wordClassName,
                    "tw-transition-all tw-duration-300",
                    {
                      "tw-text-purple-700": isPast || isActive,
                      "tw-text-gray-400": isFuture,
                    },
                  )}
                  style={{
                    opacity: isPast || isActive ? 1 : 0.5,
                  }}
                >
                  {word?.word}
                </span>
              );
            })}
          </div>
        ) : (
          <div className="tw-relative tw-w-full tw-text-center tw-text-wrap">
            {nextSegment ? (
              nextSegment.words.map((word, index) => (
                <span
                  key={`${nextSegment.id}-${index}`}
                  className={cn(
                    baseLyricClassName,
                    wordClassName,
                    "tw-text-gray-400",
                  )}
                  style={{ opacity: 0.5 }}
                >
                  {word?.word}
                </span>
              ))
            ) : (
              <span
                className={cn(baseLyricClassName, "tw-text-gray-400")}
                style={{ opacity: 0.5 }}
              >
                ...
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
